/**
 * Who Am I? browser-source entrypoint (continuous-play variant).
 *
 * Wires WS → reducer → DOM scenes + theme/audio/UI overlays. Top-left
 * countdown shows the 60s round timer (pointing at `ends_at_ms`); side-LB
 * auto-cycles between "ALL TIME · TOP 50" and "THIS SESSION".
 *
 * URL params:
 *   key   — API key (required for non-mock mode)
 *   ws    — WS port (default 3001) for local dev
 *   mock=1 — skip WS and render the lobby preview only
 */

import type { LbRow, OutMsg } from '@sg/shared';
import { readViewerMode, wsUrlFor } from '@sg/shared';
import { WsClient } from './ws/client.js';
import { resolveWsBase } from './ws/ws-base.js';
import { Store } from './state/store.js';
import type { SceneName } from './state/types.js';
import { createDomStage } from './scene/lib/stage.js';
import { applyTheme, DEFAULT_THEME } from './scene/lib/theme.js';
import { createLobby } from './scene/lobby.js';
import { createRound } from './scene/round.js';
import { createReveal } from './scene/reveal.js';
import { createPodium } from './scene/podium.js';
import { createConnectionLost } from './scene/connection-lost.js';
import { audioBus } from './audio/bus.js';
import { sfx as sfxImpl } from './audio/sfx.js';
import { music as musicImpl } from './audio/music.js';
import { mountAudioUnlock } from './ui/audio-unlock.js';
import { mountFullscreenButton } from './ui/fullscreen-button.js';
import { createAnimatedBg } from './ui/animated-bg.js';
import { createSparkles } from './ui/sparkles.js';
import { createSideLeaderboard } from './ui/side-leaderboard.js';
import { createCountdown } from './ui/countdown.js';
import { createRoundSplash } from './ui/round-splash.js';
import { createGuessBubbles } from './ui/guess-bubbles.js';
import { createConflictBanner } from './ui/conflict-banner.js';
import { mountWatchQrWidget } from '../../../shared/qrcode-generator/watch-qr.js';

/** Slug of THIS browser source — used to filter `cross_game_conflict` events. */
const MY_SLUG = 'who-am-i';

applyTheme(DEFAULT_THEME);

const params = new URL(window.location.href).searchParams;
const mockMode = params.get('mock') === '1';
// Viewer mode (`?viewer=1&handle=<HANDLE>`) routes through the public read-only
// /watch/<handle>/feed socket and suppresses streamer-only chrome.
const viewerMode = readViewerMode(params);
const isViewer = viewerMode.kind === 'viewer';
const key = viewerMode.kind === 'streamer' ? viewerMode.key : 'dev-no-key';
const wsPort = Number(params.get('ws') ?? '3001');

// Viewer-aware audio facades — silent no-ops in viewer mode so the AudioContext
// never instantiates. See platform/shared/src/viewer-mode.ts for the pattern.
const sfx: typeof sfxImpl = isViewer
  ? ({
      preload: async () => {},
      chime: () => {},
      reveal: () => {},
      lock: () => {},
      noWinner: () => {},
      fanfare: () => {},
      tick: () => {},
    } as unknown as typeof sfxImpl)
  : sfxImpl;
const music: typeof musicImpl = isViewer
  ? ({ play: () => {}, stop: () => {} } as unknown as typeof musicImpl)
  : musicImpl;

const root = document.getElementById('root')!;
root.innerHTML = '';
root.style.cssText = 'position: relative; width: 100vw; height: 100vh; overflow: hidden;';
root.setAttribute('data-testid', 'browser-root');

const stage = createDomStage({ mount: root });
const stageEl = stage.el;

const animatedBg = createAnimatedBg(stageEl);
void animatedBg;

const lobby = createLobby(stageEl);
const round = createRound(stageEl);
const reveal = createReveal(stageEl);
const podium = createPodium(stageEl);
const connectionLost = createConnectionLost(stageEl);
const sparkles = createSparkles(stageEl);
const sideLb = createSideLeaderboard(stageEl);
const countdown = createCountdown(stageEl);
const roundSplash = createRoundSplash(stageEl);
// Rising guess bubbles overlay (mirrors Survey Says + Anagram). Sits at the
// bottom of the round content area, inside the 1180px budget so it doesn't
// fight the right-side leaderboard. Spawn is gated on scene === 'round'.
const guessBubbles = createGuessBubbles(stageEl);
// Cross-game conflict banner — backend fans `cross_game_conflict` to every
// connected browser source for the tenant; we filter by MY_SLUG below.
const conflictBanner = createConflictBanner(stageEl);
if (!isViewer) {
  mountFullscreenButton();
  mountAudioUnlock();
}

sideLb.setVisible(false);
countdown.hide();

if (!isViewer) {
  // Audio: pre-fetch + decode SFX buffers; lobby bed starts immediately so OBS
  // page-permissions kick it off without a gesture.
  void sfx.preload();
  music.play('lobby');
}

const allScenes: Record<SceneName, { setVisible(v: boolean): void }> = {
  lobby,
  round,
  reveal,
  podium,
  connection_lost: connectionLost,
};

function applyScene(next: SceneName): void {
  for (const [name, h] of Object.entries(allScenes)) {
    h.setVisible(name === next);
  }
  root.setAttribute('data-scene', next);
  (window as unknown as { __wai_scene?: SceneName }).__wai_scene = next;
  sideLb.setVisible(next === 'round' || next === 'reveal');
  if (next !== 'round') countdown.hide();
  // Music routing: lobby bed in lobby; soft round bed during play; silent on
  // reveal/podium so the SFX cues (lock, fanfare) sit cleanly.
  if (next === 'lobby') {
    music.play('lobby');
  } else if (next === 'round') {
    music.play('round');
  } else {
    music.stop();
  }
}

const store = new Store();
applyScene(store.getState().scene);

let lastLb: LbRow[] = [];
// Drives sfx.tick() in the final 5s of each round.
let roundEndsAt = 0;
let lastRoundTickSecond = -1;
let watchQrCtrl: ReturnType<typeof mountWatchQrWidget> | null = null;
let showWatchQr = true;
let currentHandle: string | null = null;

function applyWatchQrVisibility(handle: string | null): void {
  if (isViewer) return;
  if (!showWatchQr) {
    if (watchQrCtrl) { watchQrCtrl.destroy(); watchQrCtrl = null; }
    return;
  }
  if (watchQrCtrl) {
    watchQrCtrl.update(handle);
  } else {
    watchQrCtrl = mountWatchQrWidget({ handle });
  }
}

function handleWs(msg: OutMsg): void {
  store.dispatchWs(msg);
  switch (msg.type) {
    case 'state_snapshot': {
      const snapSettings = (msg.state as any).settings;
      const nextShow = snapSettings && typeof snapSettings.show_watch_qr === 'boolean' ? snapSettings.show_watch_qr : true;
      if (nextShow !== showWatchQr) {
        showWatchQr = nextShow;
        applyWatchQrVisibility(currentHandle);
      }
      const s = msg.state;
      // Viewer mode is muted by design — skip the optimistic unlock.
      if (!isViewer && !audioBus.isUnlocked()) void audioBus.unlock();
      const rows = (s.leaderboard_top10 ?? []) as LbRow[];
      if (rows !== lastLb) {
        lastLb = rows;
      }
      // Feed the all-time top-50 pane from the snapshot's leaderboard_all_time.
      const allTime = (s.leaderboard_all_time ?? []) as LbRow[];
      sideLb.setAllTimeRows(allTime.slice(0, 50));
      break;
    }
    case 'wai_round_open': {
      sfx.chime();
      // Round splash with subject-name-free label (subject reveals via tiles).
      const subtitle = msg.category ? msg.category.toUpperCase() : '';
      roundSplash.show('NEW ROUND', subtitle);
      sparkles.burst({ x: 760, y: 540, count: 40, speed: 1.0 });
      // Top-left countdown: the round timer (ends_at_ms is authoritative).
      // countdown_starts_at_ms (when set + future) holds the visible timer at
      // the full duration until the broadcast-relay grace passes, so viewers
      // see the FULL round on screen (not duration - delay).
      countdown.setPhase('Round', msg.ends_at_ms, msg.countdown_starts_at_ms);
      roundEndsAt = msg.ends_at_ms;
      lastRoundTickSecond = -1;
      break;
    }
    case 'wai_letter_reveal': {
      sfx.reveal();
      // Fire a sparkle burst at EACH tile whose letter just got revealed.
      // The Hangman-style mechanic reveals every position of the same letter
      // at once (e.g. revealing 'E' in "EAGLE" lights up 2 tiles), so we
      // cascade a small burst per matching tile (50ms apart) instead of one
      // big burst at screen center.
      const letter = String(msg.letter ?? '').toUpperCase();
      // Defer one frame so the round scene has rebuilt the tile DOM with the
      // newly-revealed letter (the reducer runs first; DOM rebuild happens in
      // the same task via the store subscriber, but reading rects after a RAF
      // is safer across browsers).
      requestAnimationFrame(() => {
        const tiles = Array.from(
          document.querySelectorAll<HTMLDivElement>(
            '[data-testid="wai-tiles"] .wai-tile.revealed.just-revealed',
          ),
        ).filter((el) => (el.textContent ?? '').trim().toUpperCase() === letter);
        // Fallback: if the .just-revealed marker has cleared (animation
        // already kicked off), match by letter against all revealed tiles.
        const matches = tiles.length > 0
          ? tiles
          : Array.from(
              document.querySelectorAll<HTMLDivElement>(
                '[data-testid="wai-tiles"] .wai-tile.revealed',
              ),
            ).filter((el) => (el.textContent ?? '').trim().toUpperCase() === letter);
        if (matches.length === 0) {
          // Last-resort fallback: a single small center burst.
          sparkles.burst({ x: 960, y: 540, count: 20, speed: 0.9 });
          return;
        }
        const stageNode = document.getElementById('wai-stage');
        const stageRect = stageNode?.getBoundingClientRect();
        const stageScale = (window as unknown as { __wai_stage_scale?: number }).__wai_stage_scale ?? 1;
        for (let i = 0; i < matches.length; i++) {
          const el = matches[i]!;
          const r = el.getBoundingClientRect();
          // Convert viewport rect -> stage coords (sparkles canvas lives at
          // 1920x1080 inside the scaled stage, so divide by stageScale).
          const cxPx = r.left + r.width / 2;
          const cyPx = r.top + r.height / 2;
          const x = stageRect ? (cxPx - stageRect.left) / stageScale : cxPx;
          const y = stageRect ? (cyPx - stageRect.top) / stageScale : cyPx;
          setTimeout(() => {
            sparkles.burst({ x, y, count: 22, speed: 0.85 });
          }, i * 50);
        }
      });
      // Round timer keeps running — don't override the countdown here.
      break;
    }
    case 'wai_correct_answer': {
      sfx.lock();
      sparkles.burst({ x: 760, y: 540, count: 80, speed: 1.4 });
      break;
    }
    case 'wai_round_end': {
      sparkles.burst({ x: 760, y: 540, count: 60, speed: 1.3 });
      countdown.hide();
      roundEndsAt = 0;
      // Clear in-flight rising bubbles so they don't bleed into the reveal scene.
      guessBubbles.clear();
      if (msg.correct_answers.length === 0) sfx.noWinner();
      break;
    }
    case 'wai_guess_received': {
      // Visual feedback only — spawn a rising bubble with the guess text.
      // Backend rate-limits to ~10 frames/sec; the overlay soft-caps concurrent
      // bubbles so the DOM stays bounded.
      if (store.getState().scene === 'round') guessBubbles.spawn(msg.guess);
      break;
    }
    case 'wai_session_leaderboard': {
      sideLb.setSessionRows(msg.rows.map((r) => ({
        rank: r.rank,
        player_id: r.player_id,
        display_name: r.display_name,
        total_points: r.total_points,
      })));
      break;
    }
    case 'wai_game_over': {
      sfx.fanfare();
      sparkles.confetti({ count: 200 });
      countdown.hide();
      break;
    }
    case 'cross_game_conflict':
      // Banner only matters for the slug being attempted OR the slug currently
      // running — other game scenes shouldn't surface the warning.
      if (msg.attempted_slug === MY_SLUG || msg.running_slug === MY_SLUG) {
        conflictBanner.show(msg.message);
      }
      break;
    case 'auth_ok' as OutMsg['type']: {
      const authMsg = msg as unknown as { type: 'auth_ok'; handle: string | null };
      currentHandle = authMsg.handle;
      applyWatchQrVisibility(authMsg.handle);
      break;
    }
    default:
      break;
  }
}

store.subscribe((next, prev) => {
  if (next.scene !== prev.scene) applyScene(next.scene);
  if (next.theme && next.theme !== prev.theme) applyTheme(next.theme);
  if (next.scene === 'lobby') {
    lobby.setLeaderboard(next.leaderboard, next.leaderboardAllTime);
  }
  if (next.scene === 'round' && next.round) {
    round.setRound(next.round);
  }
  if (next.scene === 'reveal' && next.reveal) {
    reveal.setReveal(next.reveal);
  }
  if (next.scene === 'podium' && next.podium) {
    podium.setPodium(next.podium);
  }
  // Push session standings into the side panel whenever they change.
  if (next.sessionLeaderboard !== prev.sessionLeaderboard) {
    sideLb.setSessionRows(next.sessionLeaderboard);
  }
  // All-time top-50 comes from snapshot frames (leaderboard_all_time).
  if (next.leaderboardAllTime !== prev.leaderboardAllTime) {
    sideLb.setAllTimeRows(next.leaderboardAllTime.slice(0, 50));
  }
});

setInterval(() => {
  if (store.getState().scene === 'round') round.tick();
}, 200);

// Tick SFX driver: fires sfx.tick() once per second in the final 5s of the
// round. Backend `wai_round_open` populates roundEndsAt; round_end clears it.
setInterval(() => {
  if (roundEndsAt <= 0) return;
  const remainingMs = roundEndsAt - Date.now();
  if (remainingMs <= 0 || remainingMs > 5_000) return;
  const secondsLeft = Math.ceil(remainingMs / 1000);
  if (secondsLeft !== lastRoundTickSecond && secondsLeft >= 1 && secondsLeft <= 5) {
    lastRoundTickSecond = secondsLeft;
    sfx.tick();
  }
}, 200);

let wsClient: WsClient | null = null;
function connectWs(): void {
  // Viewer mode → /watch/<handle>/feed (read-only). Streamer mode keeps the
  // existing /browser?key=...&game=who-am-i handshake.
  const wsUrl = wsUrlFor(viewerMode, resolveWsBase(wsPort), { game: 'who-am-i' });
  wsClient = new WsClient({
    url: wsUrl,
    sessionToken: key,
    viewer: isViewer,
    onMessage: handleWs,
    onState: (s) => {
      store.dispatch({ type: 'connection', value: s });
      // The watch SPA owns the connection-lost UI for viewer iframes.
      if (!isViewer) connectionLost.setVisible(s !== 'open');
    },
  });
  wsClient.connect();
  (window as unknown as Record<string, unknown>).__wai_ws = wsClient;
}

if (mockMode) {
  const fakeRows: LbRow[] = buildMockRows();
  lobby.setLeaderboard(fakeRows, fakeRows);
  sideLb.setAllTimeRows(fakeRows);
  void audioBus.unlock();
} else {
  connectWs();
}

// Auto-pause when browser source not visible (OBS scene switch / tab bg).
// Closes WS so backend soft-abort grace can end the game when no sources
// remain. Reconnects on revisible — FSM re-broadcasts state via state_snapshot.
if (!mockMode) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (wsClient) {
        try { wsClient.close(); } catch { /* swallow */ }
        wsClient = null;
      }
    } else if (document.visibilityState === 'visible') {
      if (!wsClient) connectWs();
    }
  });
}

if (!isViewer) {
  window.addEventListener(
    'pointerdown',
    () => { void audioBus.unlock(); },
    { once: true },
  );
  window.addEventListener(
    'keydown',
    () => { void audioBus.unlock(); },
    { once: true },
  );
}

(window as unknown as Record<string, unknown>).__wai_store = store;
// Expose handler so screenshot harnesses can inject synthetic frames.
(window as unknown as Record<string, unknown>).__wai_handleWs = handleWs;

function buildMockRows(): LbRow[] {
  const out: LbRow[] = [];
  const names = 'AlphaWolf,BetaTester,GammaRay,DeltaForce,EpsilonEcho,FoxtrotFan,GolfGuru,HotelHero,IndiaInk,JulietJive'.split(',');
  for (let i = 0; i < names.length; i++) {
    const name = names[i]!;
    out.push({
      rank: i + 1,
      player_id: i + 1,
      display_name: name,
      avatar_seed: name.toLowerCase(),
      total_points: 1240 - i * 90,
      games_played: 12 - Math.floor(i / 2),
      games_won: Math.max(0, 4 - Math.floor(i / 2)),
    });
  }
  return out;
}
